// src/pages/Auth.jsx
//
// Экран входа/регистрации для конкретной роли (shop или customer).
// Роль передаётся пропом `role` — выбирается на ModeSelect перед этим.
// Кнопка "назад" возвращает на ModeSelect.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { errorMessage } from '../utils/errorMessage';
import Icon from '../components/Icon';
import ForgotPassword from './ForgotPassword';
import PasswordInput from '../components/PasswordInput';

export default function Auth({ role, onBack }) {
  const { t } = useTranslation();
  const { login, registerCustomer, registerShop } = useAuth();
  const [tab, setTab] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgot, setForgot] = useState(false);

  const isShop = role === 'shop';
  const titleKey  = isShop ? 'auth.titleShop'  : 'auth.titleCustomer';
  const iconName  = isShop ? 'store' : 'star';

  const handleSubmit = async () => {
    setError('');
    if (!form.email) return setError(t('auth.errorEmailRequired'));
    if (!form.password) return setError(t('auth.errorPasswordRequired'));
    setLoading(true);
    try {
      const email = form.email.trim().toLowerCase();
      if (tab === 'login') {
        await login(email, form.password, role);
      } else {
        if (form.password.length < 6) {
          setError(t('auth.errorPasswordShort'));
          setLoading(false);
          return;
        }
        if (isShop) {
          await registerShop(email, form.password, form.name.trim());
        } else {
          await registerCustomer(email, form.password, form.name.trim());
        }
      }
    } catch (e) { setError(errorMessage(e, t)); }
    setLoading(false);
  };

  const switchTab = (newTab) => {
    setTab(newTab);
    setError('');
    setForm({ email: '', password: '', name: '' });
  };

  // Экран сброса пароля — та же роль, что и на этом экране входа
  if (forgot) {
    return (
      <ForgotPassword
        role={role}
        initialEmail={form.email}
        onBack={() => setForgot(false)}
        onDone={() => { setForgot(false); setTab('login'); setError(''); }}
      />
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      padding: 24,
      background: 'var(--bg)',
    }}>
      {/* Кнопка "назад" */}
      <button
        onClick={onBack}
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top) + 12px)',
          left: 12,
          background: 'none',
          border: 'none',
          padding: 10,
          cursor: 'pointer',
          color: 'var(--fg-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 14,
          fontFamily: 'var(--font-display)',
        }}
      >
        <Icon name="chevronLeft" size={20} stroke={2.5} />
        {t('common.back')}
      </button>

      {/* Шапка */}
      <div style={{ marginTop: 'calc(env(safe-area-inset-top) + 56px)', textAlign: 'center', marginBottom: 28 }}>
        <div style={{
          width: 64, height: 64, margin: '0 auto 14px',
          background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-accent))',
          borderRadius: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(99, 102, 241, 0.25)',
          color: '#fff',
        }}>
          <Icon name={iconName} size={32} stroke={2} />
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em' }}>
          {t(titleKey)}
        </h1>
      </div>

      {/* Табы вход/регистрация */}
      <div style={{
        display: 'flex',
        background: 'var(--bg-subtle)',
        padding: 4,
        borderRadius: 'var(--r-sm)',
        marginBottom: 20,
      }}>
        <TabBtn active={tab === 'login'}    onClick={() => switchTab('login')}>{t('auth.signIn')}</TabBtn>
        <TabBtn active={tab === 'register'} onClick={() => switchTab('register')}>{t('auth.signUp')}</TabBtn>
      </div>

      {tab === 'register' && (
        <div className="input-group">
          <label>{t('auth.name')}</label>
          <input
            className="input"
            placeholder={isShop ? t('auth.shopNamePlaceholder') : t('auth.namePlaceholder')}
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
        </div>
      )}

      <div className="input-group">
        <label>{t('auth.email')}</label>
        <input
          className="input"
          type="email"
          placeholder="you@example.com"
          autoCapitalize="none"
          autoCorrect="off"
          value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
        />
      </div>

      <div className="input-group">
        <label>{t('auth.password')}</label>
        <PasswordInput
          className="input"
          placeholder="••••••"
          value={form.password}
          onChange={e => setForm({ ...form, password: e.target.value })}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        />
      </div>

      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>
          {error}
        </p>
      )}

      <button className="btn btn-primary btn-lg" onClick={handleSubmit} disabled={loading}>
        {loading ? t('common.loading') : (tab === 'login' ? t('auth.signIn') : t('auth.signUp'))}
      </button>

      {tab === 'login' && (
        <button
          onClick={() => { setForgot(true); setError(''); }}
          style={{
            marginTop: 16,
            background: 'none',
            border: 'none',
            color: 'var(--fg-muted)',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-display)',
            alignSelf: 'center',
          }}
        >
          {t('forgot.link')}
        </button>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '10px',
        background: active ? 'var(--bg-elevated)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--r-xs)',
        fontWeight: 700,
        fontSize: 14,
        color: active ? 'var(--fg)' : 'var(--fg-muted)',
        cursor: 'pointer',
        transition: 'all 0.15s',
        boxShadow: active ? 'var(--shadow-xs)' : 'none',
        fontFamily: 'var(--font-display)',
      }}
    >
      {children}
    </button>
  );
}
